'use client';

import React from 'react';
import { styled } from '@linaria/react';

import { BREAKPOINTS } from '@/constants';

import HueWheel from './HueWheel';
import SatLitGrid from './SatLitGrid';
import ColorSnippet from './ColorSnippet';

const ColorPicker = () => {
  const [hue, setHue] = React.useState(0);
  const [saturation, setSaturation] = React.useState(100);
  const [lightness, setLightness] = React.useState(50);

  const unitSize = 275;

  return (
    <OuterWrapper>
      <Wrapper
        style={{
          '--unit-size': `${unitSize}px`,
        }}
      >
        <Row>
          <SatLitGrid
            size={unitSize}
            hue={hue}
            saturation={saturation}
            setSaturation={setSaturation}
            lightness={lightness}
            setLightness={setLightness}
          />
          <HueWheel hue={hue} setHue={setHue} size={unitSize} />
        </Row>
        <BottomRow>
          <ColorSquare
            style={{
              '--background-color': `hsl(${hue}deg ${saturation}% ${lightness}%)`,
            }}
          />
          <SnippetWrapper>
            <ColorSnippet
              hue={hue}
              saturation={saturation}
              lightness={lightness}
            />
          </SnippetWrapper>
        </BottomRow>
      </Wrapper>
    </OuterWrapper>
  );
};

const OuterWrapper = styled.div`
  --inset-by: 64px;
  display: flex;
  justify-content: center;
  width: calc(100% + var(--inset-by));
  margin-left: calc(var(--inset-by) / 2 * -1);
  margin-right: calc(var(--inset-by) / 2 * -1);

  @media (max-width: 690px) {
    --inset-by: 0px;
  }
`;

const Wrapper = styled.div`
  --gap: 32px;
  width: 100%;
  max-width: calc(var(--unit-size) * 2 + var(--gap));
  margin: 96px auto 64px;
  --white-border-thickness: 2px;
`;

const Row = styled.div`
  display: flex;
  gap: 32px;
  margin-bottom: 32px;

  @media (max-width: 690px) {
    flex-direction: column;
    align-items: center;
  }
`;

const BottomRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 32px;

  @media ${BREAKPOINTS.smAndSmaller} {
    gap: 0px;
  }
`;

const ColorSquare = styled.div`
  flex: 1 100000 6rem;
  max-width: 120px;
  min-width: 40px;
  height: 6rem;
  border-radius: 2px;
  background-color: var(--background-color);
  border: var(--white-border-thickness) solid white;
  box-shadow:
    0.6px 1.3px 2.2px rgba(0, 0, 0, 0.02),
    1.5px 3.1px 5.3px rgba(0, 0, 0, 0.028),
    2.9px 5.8px 10px rgba(0, 0, 0, 0.035),
    5.1px 10.3px 17.9px rgba(0, 0, 0, 0.042),
    9.6px 19.2px 33.4px rgba(0, 0, 0, 0.05),
    23px 46px 80px rgba(0, 0, 0, 0.07);

  /*
    On smaller screens, the CSS snippet breaks to 4 lines, so it
    becomes taller
  */
  @media (max-width: 690px) {
    flex-basis: 7.5rem;
    height: 7.5rem;
  }

  @media ${BREAKPOINTS.smAndSmaller} {
    border-right: none;
    border-radius: 2px 0 0 2px;
  }
`;

const SnippetWrapper = styled.div`
  --inline-padding-override: 11px;
  --block-padding-override: 0;
  flex: 10000000;
  min-height: 6rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  border: var(--white-border-thickness) solid white;
  border-radius: 2px;
  background-color: var(--syntax-bg);
  box-shadow:
    0.6px 1.3px 2.2px rgba(0, 0, 0, 0.02),
    1.5px 3.1px 5.3px rgba(0, 0, 0, 0.028),
    2.9px 5.8px 10px rgba(0, 0, 0, 0.035),
    5.1px 10.3px 17.9px rgba(0, 0, 0, 0.042),
    9.6px 19.2px 33.4px rgba(0, 0, 0, 0.05),
    23px 46px 80px rgba(0, 0, 0, 0.07);

  @media (max-width: 690px) {
    min-height: 7.5rem;
    min-width: 17.625rem;
    border-radius: 0px 2px 2px 0px;
  }
`;

export default ColorPicker;
